<template>
  <div v-if="hasAnyPermission('faq-view')" class="container-fluid py-4">
    <div class="row g-4">
      <!-- Left Sidebar - FAQ List -->
      <div class="col-lg-3">
        <div class="card border-0 shadow-sm sticky-top" style="top: 100px;">
          <div class="card-body p-4">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div class="flex-grow-1 me-2">
                <h5 class="fw-bold mb-2">FAQ</h5>
                <p class="text-muted text-sm mb-0">Browse through common questions</p>
              </div>
              <button
v-tooltip.bottom="'Back to FAQ List'" type="button" 
                class="btn btn-sm btn-light rounded-circle p-0 flex-shrink-0"
                style="width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;" @click="$router.back()">
                <i class="pi pi-arrow-left text-dark"></i>
              </button>
            </div>
            
            <!-- Search FAQ -->
            <div class="mb-3">
              <InputText v-model="searchQuery" placeholder="Search FAQs..." class="w-100" size="small">
                <template #prefix>
                  <i class="pi pi-search"></i>
                </template>
              </InputText>
            </div>

            <!-- FAQ List -->
            <div class="faq-list" style="max-height: 500px; overflow-y: auto;">
              <div v-if="loadingList">
                <Skeleton v-for="i in 5" :key="i" height="3rem" class="mb-2"></Skeleton>
              </div>
              <div v-else>
                <div 
                  v-for="item in filteredFaqList" 
                  :key="item.id"
                  class="faq-item p-3 mb-2 rounded cursor-pointer"
                  :class="{ 'active': selectedFaqId === item.id }"
                  @click="selectFaq(item.id)"
                >
                  <div class="d-flex align-items-start gap-2">
                    <i class="pi pi-question-circle mt-1 text-muted"></i>
                    <span class="text-sm fw-semibold">{{ item.question }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Content - FAQ Detail -->
      <div class="col-lg-9">
        <div class="card border-0 shadow-sm">
          <div class="card-body p-5">
            <!-- Loading State -->
            <div v-if="loading">
              <Skeleton height="3rem" class="mb-4"></Skeleton>
              <Skeleton height="1rem" width="30%" class="mb-4"></Skeleton>
              <Skeleton height="15rem"></Skeleton>
            </div>

            <!-- FAQ Content -->
            <div v-else-if="faq">
              <h4 class="fw-bold mb-3 text-dark">{{ faq.question }}</h4>
              
              <!-- Meta Info -->
              <div class="d-flex align-items-center gap-3 mb-4 pb-4 border-bottom">
                <div class="d-flex align-items-center gap-2 text-muted">
                  <i class="pi pi-calendar text-sm"></i>
                  <span class="text-sm">{{ formatDate(faq.created_at) }}</span>
                </div>
                <div v-if="faq.updated_at && faq.updated_at !== faq.created_at" class="d-flex align-items-center gap-2 text-muted">
                  <i class="pi pi-refresh text-sm"></i>
                  <span class="text-sm">Updated {{ formatDate(faq.updated_at) }}</span>
                </div>
              </div>

              <!-- Answer Content -->
              <div class="faq-answer-content" v-html="faq.answer"></div>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center py-5">
              <i class="pi pi-info-circle text-muted" style="font-size: 3rem;"></i>
              <h5 class="mt-3 mb-2">Select a question</h5>
              <p class="text-muted">Choose a question from the list to view the answer</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import api from "@/helpers/api";
import faqService from "@/services/faqService";
import NoAccess from "@/components/NoAccess.vue";

export default {
  name: 'FaqDetail',
  components: { NoAccess },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    filteredFaqList() {
      if (!this.searchQuery) return this.faqList;
      const query = this.searchQuery.toLowerCase();
      return this.faqList.filter(f =>
        f.question.toLowerCase().includes(query) ||
        f.answer.toLowerCase().includes(query)
      );
    }
  },
  data() {
    return {
      faq: null,
      loading: false,
      loadingList: true,
      faqList: [],
      selectedFaqId: null,
      searchQuery: '',
    };
  },
  mounted() {
    if (this.hasAnyPermission('faq-view')) {
      this.loadFaqList();
      if (this.$route.params.id) {
        this.selectedFaqId = parseInt(this.$route.params.id);
        this.getFaqDetail(this.selectedFaqId);
      }
    }
  },
  updated() {
    // Force breadcrumb update after FAQ loaded
    if (this.faq && this.faq.question && this.$route.meta.title !== this.faq.question) {
      this.$route.meta.title = this.faq.question;
      this.$forceUpdate();
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    async loadFaqList() {
      this.loadingList = true;
      try {
        const response = await api(this, faqService.list({ limit: 0 }), false);
        this.faqList = response.data.data || response.data || [];
      } catch (error) {
        console.error('Error loading FAQ list:', error);
        this.faqList = [];
      } finally {
        this.loadingList = false;
      }
    },
    async getFaqDetail(id) {
      this.loading = true;
      try {
        const response = await api(this, faqService.detail(id), false);
        this.faq = response.data;
        
        // Update route meta for breadcrumb
        if (this.faq && this.faq.question) {
          this.$route.meta.title = this.faq.question;
          // Force re-render breadcrumb
          await this.$nextTick();
          window.dispatchEvent(new Event('route-meta-updated'));
        }
      } catch (error) {
        console.error('Error loading FAQ:', error);
        this.faq = null;
      } finally {
        this.loading = false;
      }
    },
    selectFaq(id) {
      this.selectedFaqId = id;
      this.getFaqDetail(id);
      // Update URL without reload
      this.$router.replace({ name: 'FAQ Detail', params: { id } });
    },
    formatDate(date) {
      return date ? moment(date).format("DD MMMM YYYY") : "-";
    },
  },
};
</script>

<style scoped>
.faq-answer-content {
  font-size: 1rem;
  line-height: 1.8;
  color: #344767;
}

.faq-answer-content :deep(h1),
.faq-answer-content :deep(h2),
.faq-answer-content :deep(h3) {
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  color: #344767;
}

.faq-answer-content :deep(h1) {
  font-size: 1.75rem;
}

.faq-answer-content :deep(h2) {
  font-size: 1.5rem;
}

.faq-answer-content :deep(h3) {
  font-size: 1.25rem;
}

.faq-answer-content :deep(p) {
  margin-bottom: 1rem;
}

.faq-answer-content :deep(ul),
.faq-answer-content :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.faq-answer-content :deep(img) {
  display: block !important;
  margin-left: auto !important;
  margin-right: auto !important;
  max-width: 100%;
  max-height: 400px;
  height: auto;
}

.faq-answer-content :deep(p img) {
  display: block !important;
  margin-left: auto !important;
  margin-right: auto !important;
}

.faq-answer-content :deep(li) {
  margin-bottom: 0.5rem;
}

.faq-answer-content :deep(a) {
  color: #5e72e4;
  text-decoration: none;
}

.faq-answer-content :deep(a:hover) {
  text-decoration: underline;
}

.faq-answer-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  margin: 1rem 0;
}

.faq-answer-content :deep(strong) {
  font-weight: 600;
}

.text-sm {
  font-size: 0.875rem;
}

.faq-item {
  transition: all 0.2s ease;
  border: 1px solid transparent;
  background-color: transparent;
}

.faq-item:hover {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.faq-item.active {
  background-color: #5e72e4;
  border-color: #5e72e4;
  color: white;
}

.faq-item.active .text-muted {
  color: rgba(255, 255, 255, 0.8) !important;
}

.faq-item.active span {
  color: white;
}

.cursor-pointer {
  cursor: pointer;
}

.faq-list {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.faq-list:hover {
  scrollbar-color: #888 #f1f1f1;
}

.faq-list::-webkit-scrollbar {
  width: 6px;
}

.faq-list::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

.faq-list::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 10px;
}

.faq-list:hover::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.faq-list:hover::-webkit-scrollbar-thumb {
  background: #888;
}

.faq-list:hover::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
